import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { MAP_RESTRICTION } from '../../../../../../app/store/constant';
import { updateEventById } from '../../../../../../app/store/slices/event/event';
import { useEventStatus } from '../../../../../../hooks/event-hooks/useEventStatus';
import { errorToast, successToastTopRight } from '../../../../../../utils/toasts/toasts';

export default function useCustomLocation() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { eventData } = useSelector((state) => state.event);
    const { cities, states, countries } = useSelector((state) => state.cities);
    const { user } = useSelector((state) => state.auth);
    const { tickets } = useSelector((state) => state.ticket);
    const [googleSuggestOption, setGoogleSuggestOption] = useState([]);
    const [mapDragData, setMapDragData] = useState('');
    const [googleSelectedValue, setGoogleSelectedValue] = useState(null);
    const [openTicketPopup, setOpenTicketPopup] = useState(false);
    const [openLocationSkipModal, setOpenLocationSkipModal] = useState(false);
    const [isDiffMoreThan24Hours, setIsDiffMoreThan24Hours] = useState(false);
    const [isEditModeOn, setIsEditModeOn] = useState(true);
    const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
    const [loading, setLoading] = useState(false);

    const { isEventDrafted, isLocationTabCompleted, isEventLive } = useEventStatus();

    useEffect(() => {
        const shouldShow = isEventDrafted && isLocationTabCompleted;
        setShouldShowEditButton(shouldShow);

        if (eventData?.publish || isEventLive) {
            // If published → always false
            setIsEditModeOn(false);
        } else if (!isLocationTabCompleted || !isEventDrafted) {
            // If not completed OR not drafted → allow editing
            setIsEditModeOn(true);
        } else {
            // Both drafted & completed → disable edit mode
            setIsEditModeOn(false);
        }
    }, [isEventDrafted, isEventLive, isLocationTabCompleted, eventData?.publish]);

    // const disabledFields = useMemo(() => {
    //     return !isEditModeOn || eventData?.publish === true || !formik.values.address;
    // }, [eventData, formik.values.address, isEditModeOn]);


    function handleTicketRedirect() {
        navigate(`/event/${eventData?.slug}/tickets`);
        setOpenTicketPopup(false);
    }

    function handleSkipForNow() {
        setOpenTicketPopup(false);
        navigate(`/event/${eventData?.slug}/dashboard`);
    }

    // const initialValues = {
    //     searchedVenue: "",
    //     venueName: eventData?.venue || "",
    //     address: eventData?.address || "",
    //     // city: eventData?.city?.id ? "Surat" : "",
    //     // state: eventData?.state?.id ? "Gujarat" : "",
    //     city: cities.find(city => city.id === eventData?.city_id)?.name || "",
    //     state: states.find(state => state.id === eventData?.state_id)?.name || "",
    //     postalCode: eventData?.zipcode || "",
    //     street: eventData?.street || "",
    //     area: eventData?.area || "",
    //     latitude: eventData?.latitude || "",
    //     longitude: eventData?.longitude || "",
    //     // country: eventData?.country_id ? "India" : "",
    //     country: countries.find(country => country.id === eventData?.country_id)?.name || "",
    // }

    const initialValues = useMemo(
        () => ({
            searchedVenue: '',
            venueName: eventData?.venue || '',
            address: eventData?.address || '',
            city: cities?.find((city) => city.id === eventData?.city_id)?.name || '',
            state: states?.find((state) => state.id === eventData?.state_id)?.name || '',
            postalCode: eventData?.zipcode || '',
            street: eventData?.street || '',
            area: eventData?.area || '',
            latitude: eventData?.latitude || '',
            longitude: eventData?.longitude || '',
            country: countries?.find((country) => country.id === eventData?.country_id)?.name || ''
        }),
        [eventData, cities, states, countries]
    ); // ✅ Add dependencies

    // console.log("Initial Values -> ", initialValues)
    // console.log("Cities loaded:", cities?.length)
    // console.log("States loaded:", states?.length)
    // console.log("Countries loaded:", countries?.length)

    // console.log("Initial Values -> ", initialValues)

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const payload = {
                    step: 'location',
                    user_id: user?.id,
                    venue: values.venueName,
                    address: values.address,
                    latitude: values.latitude,
                    longitude: values.longitude,
                    street: values.street,
                    area_id: values.area,
                    city_id: values.city,
                    state_id: values.state,
                    zipcode: values.postalCode,
                    country_id: values.country
                };

                await dispatch(
                    updateEventById({
                        eventId: eventData?.eventId || 70,
                        eventData: payload
                    })
                ).unwrap();

                successToastTopRight('Event location updated successfully');

                // setOpenTicketPopup(true);
                if (tickets?.length === 0) {
                    setOpenTicketPopup(true);
                } else {
                    setOpenTicketPopup(false);
                }
            } catch (error) {
                errorToast(error?.message || 'Failed to update event location');
            } finally {
                setLoading(false);
            }
        }
    });

    function setFieldsValue() {
        const newFields = mapDragData
            ? {
                venueName: mapDragData.venueName || '',
                address: mapDragData.address || '',
                city: mapDragData.city || '',
                postalCode: mapDragData.postalCode || '',
                state: mapDragData.state || '',
                area: mapDragData.area || '',
                street: mapDragData.street || '',
                latitude: mapDragData.lat || '',
                longitude: mapDragData.lng || '',
                country: mapDragData.country || ''
            }
            : googleSelectedValue
                ? {
                    venueName: googleSelectedValue.venueName || '',
                    address: googleSelectedValue.address || '',
                    city: googleSelectedValue.city || '',
                    postalCode: googleSelectedValue.postalCode || '',
                    state: googleSelectedValue.state || '',
                    area: googleSelectedValue.area || '',
                    street: googleSelectedValue.street || '',
                    latitude: googleSelectedValue.latitude || '',
                    longitude: googleSelectedValue.longitude || '',
                    country: googleSelectedValue.country || ''
                }
                : {
                    venueName: '',
                    address: '',
                    city: '',
                    postalCode: '',
                    state: '',
                    area: '',
                    street: '',
                    latitude: '',
                    longitude: '',
                    country: ''
                };

        formik.setValues({
            ...formik.values,
            ...newFields
        });
    }

    // useEffect(() => {
    //     setFieldsValue();
    // }, [mapDragData, formik.values.searchedVenue, googleSelectedValue, eventData]);

    useEffect(() => {
        if (mapDragData || googleSelectedValue) {
            setFieldsValue();
        }
    }, [mapDragData, googleSelectedValue]);

    // function to handle the custom location fields value change
    function handleFieldInputChange(event) {
        const { name, value } = event.target;
        formik.setFieldValue(name, value);
    }

    // useEffect to fetch places that matches with user input and set in the state
    useEffect(() => {
        let isMounted = true; // Track if the component is mounted

        const fetchPlaces = debounce(async (input) => {
            if (!input) {
                return false;
            }

            try {
                const { AutocompleteSuggestion, AutocompleteSessionToken } =
                    await window.google.maps.importLibrary('places');

                const sessionToken = new AutocompleteSessionToken();

                // console.log("Session Token : ", sessionToken);

                const request = {
                    input: input,
                    sessionToken: sessionToken,
                    locationRestriction: MAP_RESTRICTION.cityRestriction.surat
                };
                const { suggestions } =
                    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

                if (suggestions?.length) {
                    const placesWithDetails = await Promise.all(
                        suggestions.map(async (suggestion) => {
                            // Get mainText and secondaryText from the prediction
                            const mainText = suggestion.placePrediction.mainText?.text || '';
                            const secondaryText =
                                suggestion.placePrediction.secondaryText?.text || '';

                            // Convert to Place and fetch additional details
                            const place = suggestion.placePrediction.toPlace();
                            await place.fetchFields({
                                fields: ['id', 'addressComponents', 'location']
                            });

                            const getAddressComponent = (type) => {
                                if (!place.addressComponents) return '';

                                const component = place.addressComponents.find((comp) =>
                                    comp.types.includes(type)
                                );

                                return component ? component.longText : '';
                            };

                            const address = {
                                country: getAddressComponent('country'),
                                city: getAddressComponent('locality'),
                                state: getAddressComponent('administrative_area_level_1'),
                                postalCode: getAddressComponent('postal_code'),
                                area: getAddressComponent('sublocality_level_1')
                                    ? getAddressComponent('sublocality_level_1')
                                    : getAddressComponent('sublocality')
                                        ? getAddressComponent('sublocality')
                                        : getAddressComponent('political'),
                                street: getAddressComponent('route')
                                    ? getAddressComponent('route')
                                    : getAddressComponent('sublocality')
                                        ? getAddressComponent('sublocality_level_2')
                                        : getAddressComponent('political')
                            };

                            // Add the text fields to the place object
                            // const placeData = place.Eg;
                            // placeData.mainText = mainText;
                            // placeData.secondaryText = secondaryText;
                            // placeData.latitude = place.location?.lat();
                            // placeData.longitude = place.location?.lng();
                            // placeData.venueName = mainText;
                            // placeData.address = secondaryText;
                            // placeData.street = address.street;
                            // placeData.area = address.area;
                            // placeData.city = address.city;
                            // placeData.state = address.state;
                            // placeData.postalCode = address.postalCode;
                            // placeData.country = address.country;
                            // return placeData;

                            const placeData = {
                                mainText,
                                secondaryText,
                                latitude: place.location?.lat?.() || '',
                                longitude: place.location?.lng?.() || '',
                                venueName: mainText,
                                address: secondaryText,
                                street: address.street,
                                area: address.area,
                                city: address.city,
                                state: address.state,
                                postalCode: address.postalCode,
                                country: address.country
                            };

                            return placeData;

                        })
                    );

                    setGoogleSuggestOption(placesWithDetails);
                } else {
                    setGoogleSuggestOption([]);
                }
            } catch (error) {
                console.error('Error fetching places:', error);
                return [];
            }
        }, 1000);

        if (formik.values.searchedVenue?.length > 0) {
            fetchPlaces(formik.values.searchedVenue);
        } else {
            fetchPlaces.cancel();
        }

        return () => {
            fetchPlaces.cancel();
            isMounted = false;
        };
    }, [formik.values.searchedVenue]);

    useEffect(() => {
        if (eventData?.start_date && eventData?.start_time) {
            const eventStartDateAndTime = dayjs(
                `${eventData?.start_date} ${eventData?.start_time}`,
                'YYYY-MM-DD HH:mm'
            );
            const currentDateTime = dayjs();

            // Calculate the difference in hours
            const diffInHours = eventStartDateAndTime.diff(currentDateTime, 'hour');

            // Check if the difference is more than 24 hours
            // const isMoreThanOneDay =
            //     diffInHours > 24 ? true : false;

            // // console.log('Is more than 24 hours until event start:', isMoreThanOneDay);
            // return isMoreThanOneDay;

            if (diffInHours > 24) {
                setIsDiffMoreThan24Hours(true);
            } else {
                setIsDiffMoreThan24Hours(false)
            }
        }
    }, [eventData?.start_date, eventData?.start_time]);

    return {
        // disabledFields,
        loading,
        googleSuggestOption,
        formik,
        setMapDragData,
        handleFieldInputChange,
        setGoogleSelectedValue,
        googleSelectedValue,
        isEditModeOn,
        setIsEditModeOn,
        openTicketPopup,
        handleSkipForNow,
        handleTicketRedirect,
        setOpenTicketPopup,
        openLocationSkipModal,
        setOpenLocationSkipModal,
        isDiffMoreThan24Hours,
        shouldShowEditButton
    };
}

const validationSchema = Yup.object({
    // searchedVenue: Yup.string()
    //     .required("Venue name is required"),
    searchedVenue: Yup.string().when('venueName', {
        is: (venueName) => !venueName, // If venueName is empty, make searchedVenue required
        then: (schema) => schema.required('Venue name is required'),
        otherwise: (schema) => schema.notRequired()
    }),
    venueName: Yup.string().required('Venue name is required'),
    address: Yup.string().required('Address is required'),
    city: Yup.string(),
    state: Yup.string(),
    postalCode: Yup.string(),
    street: Yup.string(),
    area: Yup.string(),
    latitude: Yup.string().required('Latitude is required'),
    longitude: Yup.string().required('Longitude is required'),
    country: Yup.string().required('Country is required')
});
